import {
  DownloadOutlined,
  DrawRounded,
  LogoutOutlined,
} from "@mui/icons-material";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { grey } from "@mui/material/colors";
import { pdf } from "@react-pdf/renderer";
import { useStytch } from "@stytch/react";
import { format } from "date-fns";
import { isArray, isEmpty, isFunction, isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import DashboardLogo from "../assets/DashboardLogo";
import AnimatedCheckmark from "../components/feedback/AnimatedCheckmark/AnimatedCheckMark";
import { RffSectionAuthorizationSignature } from "../components/finalForm/common/CreditAuthorizationFormQuestions";
import RffFormStepper from "../components/finalForm/RffFormStepper";
import Flx from "../components/layout/Flx";
import BasicModal from "../components/modals/BasicModal";
import FinancingRequestPDF from "../components/pdf/FinancingRequestPDF";
import Htag from "../components/typography/Htag";
import Txt from "../components/typography/Txt";
import { useEndpointHook } from "../hooks/useEndpointHook";
import { profileSet } from "../redux/actions/profileActions";
import { financingRequestAddToEnd } from "../redux/reducers/financingRequestsReducer";
import BorrowersSection from "../screens/NewFinancingRequestScreen/BorrowersSection";
import BorrowingEntitiesSection from "../screens/NewFinancingRequestScreen/BorrowingEntitiesSection";
import FileUploadSection from "../screens/NewFinancingRequestScreen/FileUploadSection";
import LoanDetailSection from "../screens/NewFinancingRequestScreen/LoanDetailSection";
import PropertiesSection from "../screens/NewFinancingRequestScreen/PropertiesSection";
import TitledLoanSection from "../screens/NewFinancingRequestScreen/TitledLoanSection";

const LOAN_CONTACT_ROLES = [
  "Appraisal Point of Contact",
  "Title Agent",
  "Insurance Agent",
];

const StagedApplicationWizard = ({
  stagedApplication,
  show = false,
  onClose,
}) => {
  const profile = useSelector((state) => state?.profile);
  const stagedApplications = profile?.stagedApplications;
  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // ---------
  const [success, setSuccess] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);

  const {
    loading,
    createNewFinancingRequest,
    getBorrowerFinancingApplications,
    getProfile,
  } = useEndpointHook();

  const handleDownloadPdf = () => {
    if (!pdfBlob) return;
    const downloadUrl = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `Loan_Application_${format(new Date(), "MM-dd-yyyy")}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  };
  const cleanNoneValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(cleanNoneValues);
    }
    if (
      obj !== null &&
      typeof obj === "object" &&
      !(obj instanceof Date) &&
      !(obj instanceof File) &&
      !(obj instanceof Blob)
    ) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key,
          value === "None" ? null : cleanNoneValues(value),
        ]),
      );
    }
    return obj;
  };
  const handleReturnHome = (res) => {
    const updatedProfile = res?.user;
    const newLoanData = res?.created_loan;

    if (!isNil(updatedProfile)) {
      dispatch(profileSet(updatedProfile));
    } else {
      getProfile({});
    }

    if (!isNil(newLoanData)) {
      dispatch(financingRequestAddToEnd(newLoanData));
    } else {
      getBorrowerFinancingApplications({});
    }

    navigate("/");
    setSuccess(false);
  };

  const initialValues = useMemo(() => {
    if (stagedApplication) {
      let targetApp = { ...stagedApplication };
      targetApp.stagedApplicationId = targetApp?._id;
      return targetApp;
    }
    if (isArray(stagedApplications) && !isEmpty(stagedApplications)) {
      let mostRecentApp = stagedApplications[0];
      mostRecentApp.stagedApplicationId = mostRecentApp?._id;
      return mostRecentApp;
    }
  }, [stagedApplications, stagedApplication]);

  const onFormSubmit = async (submitData) => {
    let data = cleanNoneValues({ ...submitData });

    const filteredContacts = (data.loanRelatedContacts || [])
      .map((contact, i) => ({
        ...contact,
        loanRole: LOAN_CONTACT_ROLES[i],
      }))
      .filter((contact) => {
        const { loanRole, ...fields } = contact;
        return Object.values(fields).some((val) => !isNil(val) && val !== "");
      });

    let loanName = submitData?.loanName;

    if (isNil(loanName) && isArray(data?.subjectProperties)) {
      const propertyName = data?.subjectProperties[0]?.address?.fullAddress;
      if (propertyName) {
        loanName = propertyName;
      }
    }
    data.loanName = loanName;

    const blob = await pdf(<FinancingRequestPDF formData={data} />).toBlob();

    const file = new File([blob], "loan-application.pdf", {
      type: "application/pdf",
    });

    const pdfFileObj = {
      id: uuidv4(),
      file,
      name: file.name,
      file_display_name: `Loan Application - ${format(new Date(), "MM-dd-yyyy")}.pdf`,
      docGroup: "Borrower Documents",
      docType: "Loan Application",
    };

    const existingFiles = data.uploadMetadata || [];
    data.uploadMetadata = [...existingFiles, pdfFileObj];

    // console.log("SUBMIT", { pdfFileObj, data, file });

    // return;
    createNewFinancingRequest({
      data: {
        ...data,
        loanRelatedContacts: filteredContacts,
      },
      onSuccessFn: (res) => {
        setPdfBlob(blob);
        setSuccess(isNil(res) ? true : res);
      },
    });
  };

  let content = null;
  const defaultValues = useMemo(() => {
    return {
      borrowers: [{}],
      subjectProperties: [{}],
    };
  }, []);
  const successContent = (
    <Flx column fw center gap={2}>
      <Flx
        center
        column
        sx={{
          borderBottom: `1px solid ${grey[300]}`,
          py: 4,
          px: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <AnimatedCheckmark />
        </Box>
        <Htag sx={{ color: grey[900], fontWeight: 700, mb: 0.5 }}>
          Financing Request Submitted
        </Htag>
        <Typography sx={{ color: grey[700], fontSize: 14 }}>
          Your application is now being reviewed
        </Typography>
      </Flx>

      {/* Body */}
      <Box sx={{ p: 3 }}>
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: 14,
            mb: 1.5,
            lineHeight: 1.6,
          }}
        >
          A PDF copy of your loan application is available below for your
          records.
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: 14,
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          To help expedite your review, you can upload supporting documents like
          tax returns, bank statements, or property files directly from your
          loan details page.
        </Typography>

        <Flx column gap={1.5}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<DownloadOutlined />}
            onClick={handleDownloadPdf}
            disabled={!pdfBlob}
          >
            Download Loan Application PDF
          </Button>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            loading={loading}
            onClick={() => handleReturnHome(success)}
          >
            Go to My Loans
          </Button>
        </Flx>
      </Box>
    </Flx>
  );

  const formContent = (
    <Flx
      column
      fw
      center
      gap={2}
      //
      sx={{ pt: isMobile || !isFunction(onClose) ? 0 : 3 }}
    >
      {isFunction(onClose) ? null : (
        <Flx fw jb ac sx={{ position: "relative", mb: 2 }}>
          {isMobile ? (
            <DashboardLogo showSubtitle maxHeight={30} maxWidth={85} />
          ) : (
            <DashboardLogo showSubtitle height="40px" />
          )}
          <LogoutUserButton />
        </Flx>
      )}

      <Flx column fw center gap={1} sx={{ mb: 2 }}>
        <Htag tag="h1" sx={{ textAlign: "center" }} className="text-center">
          {/* Complete Your Investor Profile */}
          Review & Complete Loan Application
        </Htag>
        <Txt secondary sx={{ textAlign: "center" }} className="text-center">
          You have a staged application in progress. Please review and complete
          this application to finish setting up your profile.
        </Txt>
        {/* <Txt tag="h1" sx={{ textAlign: "center" }} className="text-center">
            Before creating your first application, let's finising setting up
            your profile.
          </Txt> */}
      </Flx>
      <RffFormStepper
        onSubmit={onFormSubmit}
        // formSpy
        // progressVariant="linear"
        submitText="Complete Application"
        loading={loading}
        initialValues={initialValues}
        // initialValues={initialValues}
        // initialValues={initialValues}
        sx={{ width: "100%" }}
        contentSx={{
          pt: 2,

          ".titled-group-body-root": {
            pt: 3,
          },
          ".titled-group-header-root": {
            h2: {
              fontSize: "22px",
              // fontSize: "22px",
              fontWeight: 800,
            },
            ".MuiSvgIcon-root": {
              fontSize: "22px",
            },
          },
        }}
      >
        <LoanDetailSection
          stepName="Loan Details"
          title="Loan Details"
          returnChildrenOnly
        />
        <PropertiesSection
          stepName="Properties"
          title="Properties"
          returnChildrenOnly
        />
        <BorrowersSection
          stepName="Borrowers"
          title="Borrowers"
          returnChildrenOnly
        />
        <BorrowingEntitiesSection
          stepName="Borrowing Entities"
          title="Borrowing Entities"
          returnChildrenOnly
        />
        <FileUploadSection
          stepName="File Upload"
          title="File Upload"
          returnChildrenOnly
        />
        <TitledLoanSection
          variant="h2"
          returnChildrenOnly
          stepName="Signature"
          icon={<DrawRounded className="thin" />}
          title="Signature & Authorization"
        >
          <RffSectionAuthorizationSignature hideTitle />
        </TitledLoanSection>
      </RffFormStepper>
    </Flx>
  );

  if (!show) {
    return null;
  }

  if (success) {
    content = successContent;
  } else {
    content = formContent;
  }
  return (
    <BasicModal
      show
      onClose={isFunction(onClose) ? onClose : undefined}
      maxWidth={success ? "sm" : "md"}
      bodySx={{ p: 3 }}
    >
      {content}
    </BasicModal>
  );
};

const LogoutUserButton = ({ children }) => {
  const navigate = useNavigate();
  const stytch = useStytch();
  const handleLogoutUser = useCallback(async () => {
    await stytch.session.revoke();
    navigate("/");
  }, [stytch, navigate]);
  return (
    <Button
      sx={{
        // position: "absolute",
        // left: -8,
        // top: 0,
        fontSize: "12px !important",
        fontWeight: 400,
      }}
      endIcon={
        <LogoutOutlined
          sx={{
            // transform: "rotate(180deg)",
            fontSize: "13px !important",
            // mr: -0.5,
          }}
        />
      }
      color="default"
      variant="text"
      size="small"
      onClick={handleLogoutUser}
    >
      Logout
    </Button>
  );
};
export default StagedApplicationWizard;
